import { shallowRef, h, createVNode, render, getCurrentInstance, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import maplibregl from 'maplibre-gl';

import { constants } from '@@/js/utils/constants';

const maplibreLocales = {
    fr: {
        'AttributionControl.ToggleAttribution': 'Afficher ou masquer les sources',
        'AttributionControl.MapFeedback': 'Commentaires sur la carte',
        'Map.Title': 'Carte',
        'Marker.Title': 'Marqueur',
        'NavigationControl.ResetBearing': 'Réorienter vers le nord',
        'NavigationControl.ZoomIn': 'Zoom avant',
        'NavigationControl.ZoomOut': 'Zoom arrière',
        'Popup.Close': 'Fermer la fenêtre',
    },
};

export const useMap = ({ popupOptions = {} } = {}) => {
    const { locale } = useI18n();
    // Popups are rendered with the app context of the map, so they get its plugins (i18n, router, pinia).
    const { appContext } = getCurrentInstance();

    const map = shallowRef(null);
    let popup = null;
    let popupNode = null;
    let userMarker = null;

    function createMap(container, options = {}) {
        map.value = new maplibregl.Map({
            container,
            style: constants.MAP_TILES_STYLE_ENDPOINT,
            maxBounds: constants.QUEBEC_CITY_BOUNDS,
            // The tile server only has data from zoom 10 to 16: zoom limits must stay within that range.
            minZoom: 11,
            maxZoom: 16,
            locale: maplibreLocales[locale.value],
            ...options,
        });

        map.value.addControl(new maplibregl.NavigationControl());
        map.value.addControl(new maplibregl.ScaleControl());

        return map.value;
    }

    function addMarker(element, lngLat) {
        const marker = new maplibregl.Marker({ element }).setLngLat(lngLat).addTo(map.value);
        element.removeAttribute('aria-label');
        element.setAttribute('aria-hidden', 'true');

        return marker;
    }

    function setUserPosition(lngLat) {
        if (!lngLat) {
            userMarker?.remove();
            userMarker = null;
        } else if (userMarker) {
            userMarker.setLngLat(lngLat);
        } else {
            const el = document.createElement('div');
            el.className = 'user-marker';
            userMarker = addMarker(el, lngLat);
        }
    }

    // A single popup is shared by the whole map; its component only lives while it is open.
    // Props can be a function: the popup then re-renders when the reactive data it reads changes.
    function openPopup(lngLat, component, props) {
        closePopup();

        if (!popup) {
            popup = new maplibregl.Popup({ ...popupOptions, closeOnClick: false });
            popup.on('close', unmountPopupContent);
        }

        const vnode = createVNode({
            render: () => h(component, typeof props === 'function' ? props() : props),
        });
        vnode.appContext = appContext;
        popupNode = document.createElement('div');
        render(vnode, popupNode);

        popup.setLngLat(lngLat).setDOMContent(popupNode).addTo(map.value);
    }

    function closePopup() {
        popup?.remove();
    }

    function unmountPopupContent() {
        if (popupNode) {
            render(null, popupNode);
            popupNode = null;
        }
    }

    // Calls the handler with the clicked feature of one of the layers. A click anywhere else closes the popup.
    // The popup's own closeOnClick is off: it would close a popup reopened by the same click.
    function onFeatureClick(layers, handler) {
        map.value.on('click', (e) => {
            const existingLayers = layers.filter((id) => map.value.getLayer(id));
            const [feature] = existingLayers.length ? map.value.queryRenderedFeatures(e.point, { layers: existingLayers }) : [];

            if (feature) {
                handler(feature, e);
            } else {
                closePopup();
            }
        });

        map.value.on('mouseenter', layers, () => {
            map.value.getCanvas().style.cursor = 'pointer';
        });
        map.value.on('mouseleave', layers, () => {
            map.value.getCanvas().style.cursor = '';
        });
    }

    async function loadImage(url) {
        const img = new Image();
        img.src = url;
        await img.decode();

        return img;
    }

    function addCanvasImage(name, width, height, draw) {
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(width * (window.devicePixelRatio || 1));
        canvas.height = Math.round(height * (window.devicePixelRatio || 1));
        const pixelRatio = canvas.width / width;
        const context = canvas.getContext('2d');
        context.scale(pixelRatio, canvas.height / height);
        draw(context);

        map.value.addImage(name, context.getImageData(0, 0, canvas.width, canvas.height), { pixelRatio });
    }

    function setGeoJSONSource(id, data) {
        const source = map.value.getSource(id);
        if (source) {
            source.setData(data);
        } else {
            map.value.addSource(id, { type: 'geojson', data });
        }
    }

    onBeforeUnmount(() => {
        closePopup();
        map.value?.remove();
    });

    return {
        map,
        createMap,
        addMarker,
        setUserPosition,
        openPopup,
        closePopup,
        onFeatureClick,
        loadImage,
        addCanvasImage,
        setGeoJSONSource,
    };
};
